import InternationalPhoneField from './InternationalPhoneField';
import { useConfirmedPhone } from '../lib/confirmedPhone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Clock, Copy, Loader2, MessageCircle, Tv, Zap } from 'lucide-react';
import { API_BASE_URL } from '../apiBase';
import { formatApiError } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { sendAccessWhatsApp } from '../lib/sendAccessWhatsApp';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import OfficeChoiceButton from './office/OfficeChoiceButton';
import PlanPicker from './PlanPicker';
import ContentModePicker from './ContentModePicker';
import { DEFAULT_ACCESS_OUTPUT } from '../lib/accessOutput';
import ContactQuickPick from './ContactQuickPick';
import { cn } from '../lib/utils';
import { createLineFromPlan, isQuickTestVisible, screenOptions, provisionScreenCount } from '../lib/createLineFromPlan';
import { planHasContentChoice } from '../lib/planContentProfile';
import PartnerAppPicker from './PartnerAppPicker';
import { PartnerAppBadge, PartnerAppPanel } from './PartnerAppIdentity';
import { buildAppSnippet, defaultCreationAppId, fillAppPlaceholders, WEB_PLAYER_OPTION_ID } from '../lib/partnerApps';
import {
  composeAccessMessage,
  ensureM3uUrl,
  pickPlanTemplate,
  DEFAULT_TRIAL_ACCESS_TEMPLATE,
} from '../lib/fillAccessTemplate';
import './ProvisionModal.css';
import { ProvisionSteps, ProvisionStep, ProvisionReview, ProvisionNavigation, useProvisionWizard } from './ProvisionWizard';
import { useWhatsAppCapability } from '../hooks/useWhatsAppCapability';

const EVENT_OPEN = 'aurax:open-quick-test';
const DEFAULT_DURACOES = [
  { id: 't_1h', label: '1h', hours: 1 },
  { id: 't_2h', label: '2h', hours: 2 },
  { id: 't_4h', label: '4h', hours: 4 },
];

async function pollJob(apiUrl, jobId, { maxMs = 180000 } = {}) {
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    const { data } = await axios.get(`${apiUrl}/api/xui/provision-jobs/${jobId}`, {
      withCredentials: true,
    });
    if (data?.status === 'done') return data.result || data;
    if (data?.status === 'failed') {
      throw new Error(data.error || 'Falha no job de provisionamento');
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
  throw new Error('Tempo esgotado ao gerar o teste');
}

function shortDurLabel(d) {
  if (!d) return '';
  if (d.label && /^\d+h$/i.test(String(d.label).trim())) return d.label;
  const h = Number(d.hours);
  if (h > 0) return `${h}h`;
  return String(d.label || '').replace(/\s*horas?/i, 'h').replace(/\s+/g, '') || d.id;
}

function browserExpiry(expDate, fallback = '') {
  const unix = Number(expDate);
  if (!Number.isFinite(unix) || unix <= 0) return fallback;
  return new Date(unix * 1000).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function buildTemplate(result, nome, planTplRaw = '', appBlock = '') {
  const block =
    appBlock ||
    result?.app_block ||
    buildAppSnippet(result?.partner_app, { detail: false }) ||
    '';
  const vars = {
    username: result?.username,
    password: result?.password,
    dns: result?.dns_label,
    expiry: result?.exp_date_formatted,
    connections: result?.max_connections || '',
    package: result?.display_name || result?.package_name || '',
    whatsapp: result?.whatsapp || '',
    notes: nome || '',
    m3u: ensureM3uUrl({
      m3u: result?.m3u || result?.m3u_url,
      dns: result?.dns_label,
      username: result?.username,
      password: result?.password,
    }),
    webplayer: result?.webplayer || result?.webplayer_url || '',
    link_renovacao: result?.link_renovacao || result?.link_pagamento || '',
    plan_price: result?.plan_price || '',
    support: result?.support || result?.support_phone || '',
    support_link: result?.support_link || '',
    info_channel: result?.info_channel || result?.info_channel_url || '',
    app_block: fillAppPlaceholders(block, {
      webplayer: result?.webplayer || result?.webplayer_url || '',
      username: result?.username,
      password: result?.password,
      dns: result?.dns_label,
    }),
  };
  return composeAccessMessage({
    apiTemplate: result?.template_msg,
    planTplRaw,
    defaultTpl: DEFAULT_TRIAL_ACCESS_TEMPLATE,
    vars,
  });
}

export default function TesteRapidoModal({ open: openProp, onOpenChange, apiUrl = API_BASE_URL }) {
  const { t } = useLanguage();
  const { automationAvailable: whatsappAutomationAvailable } = useWhatsAppCapability();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp != null ? openProp : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [planos, setPlanos] = useState([]);
  const [packages, setPackages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [duracoes, setDuracoes] = useState(DEFAULT_DURACOES);
  const [maxScreens, setMaxScreens] = useState(2);
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const phone = useConfirmedPhone(whatsapp, { resetKey: open });
  const [duracaoId, setDuracaoId] = useState('t_4h');
  const [telas, setTelas] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const requestedPlanId = useRef(null);
  const [contentMode, setContentMode] = useState('family');
  const [partnerAppId, setPartnerAppId] = useState('');
  const appChosenManually = useRef(false);
  const [partnerApps, setPartnerApps] = useState([]);
  const [webPlayerOption, setWebPlayerOption] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const createLock = useRef(false);
  const [loading, setLoading] = useState(false);
  const [sendAutoWa, setSendAutoWa] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);
  const [waSent, setWaSent] = useState(false);
  const [inlineError, setInlineError] = useState('');

  useEffect(() => {
    const handler = (e) => {
      const planId = e?.detail?.planId;
      requestedPlanId.current = planId ? String(planId) : null;
      if (planId) setSelectedPlanId(String(planId));
      setOpen(true);
    };
    window.addEventListener(EVENT_OPEN, handler);
    try {
      if (sessionStorage.getItem('aurax_open_quick_test') === '1') {
        sessionStorage.removeItem('aurax_open_quick_test');
        setOpen(true);
      }
    } catch {
      /* ignore */
    }
    return () => window.removeEventListener(EVENT_OPEN, handler);
  }, [setOpen]);

  useEffect(() => {
    if (open) setSendAutoWa(whatsappAutomationAvailable);
  }, [open, whatsappAutomationAvailable]);

  const load = useCallback(async (isCurrent) => {
    setLoading(true);
    try {
      const [planRes, pkgRes, metaRes, tmplRes, appsRes] = await Promise.all([
        axios.get(`${apiUrl}/api/xui/pricing/plans`, { withCredentials: true }),
        axios.get(`${apiUrl}/api/xui/packages`, { withCredentials: true }).catch(() => ({ data: { packages: [] } })),
        axios.get(`${apiUrl}/api/xui/categories/meta`, { withCredentials: true }).catch(() => null),
        axios.get(`${apiUrl}/api/xui/package-templates`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${apiUrl}/api/partner-apps`, { withCredentials: true }).catch(() => ({ data: { apps: [] } })),
      ]);
      if (!isCurrent()) return;
      setPlanos(planRes.data?.plans || []);
      setPackages(pkgRes.data?.packages || []);
      setTemplates(Array.isArray(tmplRes?.data) ? tmplRes.data : tmplRes?.data?.templates || []);
      const apps = Array.isArray(appsRes?.data?.apps) ? appsRes.data.apps : [];
      setPartnerApps(apps);
      if (!appChosenManually.current) setPartnerAppId(defaultCreationAppId(apps));
      setWebPlayerOption(appsRes?.data?.web_player_option || null);
      if (metaRes?.data?.duracoes_teste?.length) setDuracoes(metaRes.data.duracoes_teste);
      const cap = Number(metaRes?.data?.max_screens_test ?? metaRes?.data?.max_screens);
      if (Number.isFinite(cap) && cap > 0) {
        const next = Math.max(1, Math.min(2, cap));
        setMaxScreens(next);
        setTelas((prev) => Math.min(prev, next));
      }
    } catch (e) {
      if (isCurrent()) toast.error(formatApiError(e?.response?.data?.detail) || t('qt.fail'));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [apiUrl, t]);

  useEffect(() => {
    let current = true;
    if (open) {
      setResult(null);
      setCopied(false);
      setWaSent(false);
      setInlineError('');
      load(() => current);
    } else {
      appChosenManually.current = false;
      requestedPlanId.current = null;
    }
    return () => { current = false; };
  }, [open, load]);

  const duracao = duracoes.find((d) => d.id === duracaoId);
  const telasOpts = screenOptions(maxScreens);

  const trialPlans = useMemo(() => {
    return (planos || []).filter(
      (p) => (p.xui_package_id || p.package_id) && isQuickTestVisible(p),
    );
  }, [planos]);

  useEffect(() => {
    if (!open || loading) return;
    // An explicit rail selection must not be replaced by a stale one-plan catalog.
    if (requestedPlanId.current) return;
    if (selectedPlanId && trialPlans.some((p) => String(p.id) === String(selectedPlanId))) return;
    if (trialPlans.length === 1) setSelectedPlanId(String(trialPlans[0].id));
  }, [open, loading, trialPlans, selectedPlanId]);

  const reset = () => {
    wizard.reset();
    setInlineError('');
    setNome('');
    setWhatsapp('');
    setDuracaoId('t_4h');
    setTelas(1);
    setSelectedPlanId('');
    requestedPlanId.current = null;
    setContentMode('family');
    appChosenManually.current = false;
    setPartnerAppId(defaultCreationAppId(partnerApps));
    setResult(null);
    setCopied(false);
    setSendAutoWa(true);
    setWaSent(false);
  };

  const selectedPartnerApp = useMemo(() => {
    if (partnerAppId === WEB_PLAYER_OPTION_ID) {
      return webPlayerOption || { id: WEB_PLAYER_OPTION_ID, name: 'Só Web Player / M3U' };
    }
    return partnerApps.find((a) => String(a.id) === String(partnerAppId)) || null;
  }, [partnerAppId, partnerApps, webPlayerOption]);

  const effectiveScreens = provisionScreenCount(telas, true, partnerAppId);
  const webPlayerScreenNotice = partnerAppId === WEB_PLAYER_OPTION_ID
    ? 'O Web Player usa no mínimo 2 conexões durante o teste. A conversão mantém essa quantidade e apresenta a cotação antes de confirmar.' : '';

  const selectedPlan = trialPlans.find((p) => String(p.id) === String(selectedPlanId));
  const wizard = useProvisionWizard({ open, onError: setInlineError, issues: [
    nome.trim().length < 2 ? t('qt.needNameWa') : !phone.ready ? phone.error : '',
    !selectedPlan ? t('qt.needPlan') : '',
    !selectedPartnerApp || selectedPartnerApp.enabled === false ? t('pw.needApp') : effectiveScreens > maxScreens
      ? 'O limite de telas de teste não permite este aplicativo. Escolha outro aplicativo ou solicite ao administrador o limite de 2 conexões.' : '',
  ] });

  const deliverWa = async ({ phone, clientName, message }) => {
    setSendingWa(true);
    try {
      const res = await sendAccessWhatsApp({
        apiUrl,
        phone,
        name: clientName,
        message,
        notes: 'Teste Dragon Tech',
      });
      if (res.ok) {
        setWaSent(true);
        toast.success(t('qt.waOk'));
        return true;
      }
      toast.error(res.error || t('qt.waFail'));
      return false;
    } finally {
      setSendingWa(false);
    }
  };

  const gerar = async () => {
    if (createLock.current || submitting || loading || wizard.step !== 3) return;
    setInlineError('');
    if (!nome.trim() || nome.trim().length < 2) {
      const msg = t('qt.needNameWa');
      setInlineError(msg);
      toast.error(msg);
      return;
    }
    const wa = phone.digits;
    if (!phone.ready) {
      const msg = phone.error;
      setInlineError(msg);
      toast.error(msg);
      return;
    }
    if (!selectedPlanId) {
      const msg = t('qt.needPlan');
      setInlineError(msg);
      toast.error(msg);
      return;
    }
    if (!selectedPartnerApp || selectedPartnerApp.enabled === false) {
      const msg = 'Escolha o aplicativo do cliente (ou só Web Player / M3U).';
      setInlineError(msg);
      toast.error(msg);
      return;
    }
    createLock.current = true;
    setSubmitting(true);
    setWaSent(false);
    const loadingToast = toast.loading(t('qt.generating'));
    try {
      const plan = planos.find((p) => String(p.id) === String(selectedPlanId));
      const xui = String(plan?.xui_package_id || plan?.package_id || '').trim();
      const tmplDoc =
        templates.find((t) => String(t.catalog_plan_id || '') === String(selectedPlanId)) ||
        templates.find((t) => String(t.package_id) === xui && !String(t.catalog_plan_id || '').trim());
      const planTplRaw = pickPlanTemplate(tmplDoc, { isTrial: true });

      const out = await createLineFromPlan(apiUrl, {
        planId: selectedPlanId,
        nome: nome.trim(),
        whatsapp: wa,
        isTrial: true,
        duracaoId,
        telas,
        accessOutput: DEFAULT_ACCESS_OUTPUT,
        notes: nome.trim(),
        contentMode: planHasContentChoice(plan) ? contentMode : undefined,
        partnerAppId,
      });
      if (out?.ok !== true) {
        throw new Error(out?.message || t('qt.fail'));
      }
      const localExpiry = String(out.exp_date_formatted || '').trim() || browserExpiry(out.exp_date);
      const localizedOut = { ...out, exp_date_formatted: localExpiry };
      const enriched = {
        ...localizedOut,
        whatsapp: wa,
        partner_app: out.partner_app || selectedPartnerApp,
        template_msg: buildTemplate(localizedOut, nome.trim(), planTplRaw),
      };
      setResult(enriched);
      toast.dismiss(loadingToast);
      toast.success(t('qt.ok'));

      if (sendAutoWa && whatsappAutomationAvailable) {
        void deliverWa({
          phone: wa,
          clientName: nome.trim(),
          message: enriched.template_msg,
        });
      }
    } catch (e) {
      toast.dismiss(loadingToast);
      const msg = formatApiError(e?.response?.data?.detail) || e.message || t('qt.fail');
      setInlineError(msg);
      toast.error(msg);
    } finally {
      createLock.current = false;
      setSubmitting(false);
    }
  };

  const template = String(result?.template_msg || '').trim() || buildTemplate(result, nome);
  const resultApp = result?.partner_app || selectedPartnerApp;

  const copyTpl = async () => {
    try {
      await navigator.clipboard.writeText(template);
      setCopied(true);
      toast.success(t('qt.tplCopied'));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('qt.copyFail'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && (submitting || sendingWa)) return;
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogContent className={cn('prov-dialog', !result && 'prov-dialog--guided')}>
        <div className={cn('prov-hero', result && 'prov-hero--ok')}>
          <OfficeModalHeader
            layout="row"
            icon={Zap}
            variant="square"
            title={result ? t('qt.titleDone') : t('qt.title')}
            description={result ? t('qt.descDone') : t('pw.intro')}
          />
        </div>

        {!result && <ProvisionSteps step={wizard.step} onChange={wizard.goTo} disabled={loading || submitting} />}

        <div className={cn('prov-body', result && 'prov-body--result')}>
          {result ? (
            <>
              <div className="prov-result">
                <div className="prov-result-title">{t('qt.generated')}</div>
                <div className="prov-result-grid">
                  <Info label={t('qt.client')} value={nome || '—'} />
                  <Info label={t('qt.whatsapp')} value={whatsapp || '—'} />
                  <Info label={t('qt.screensLabel')} value={Number(result.max_connections) || effectiveScreens} />
                  <Info label={t('qt.duration')} value={shortDurLabel(duracao) || '—'} />
                  <Info label={t('qt.username')} value={result.username} mono />
                  <Info label={t('qt.expires')} value={result.exp_date_formatted || '—'} />
                  <Info label={t('qt.password')} value={result.password} mono />
                </div>
                {waSent ? (
                  <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    <Check className="h-3.5 w-3.5" /> {t('qt.waSent')}
                  </p>
                ) : null}
              </div>
              {resultApp ? (
                <div className="prov-app-compact">
                  <PartnerAppBadge labeled app={resultApp} />
                </div>
              ) : null}
              <div className="prov-msg-card">
                <div className="prov-msg-card-head">
                  <span>Mensagem pronta</span>
                  <span className="prov-msg-card-hint">WhatsApp / copiar</span>
                </div>
                <pre className="prov-msg">{template}</pre>
              </div>
              {resultApp ? (
                <div className="prov-app-detail">
                  <PartnerAppPanel
                    app={resultApp}
                    vars={{
                      webplayer: result?.webplayer || result?.webplayer_url || '',
                      username: result?.username,
                      password: result?.password,
                      dns: result?.dns_label,
                    }}
                  />
                </div>
              ) : null}
              <div className="prov-actions-row grid gap-2 sm:grid-cols-2">
                <Button type="button" variant="outline" className="w-full gap-2" onClick={copyTpl}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? t('qt.copied') : t('qt.copyTpl')}
                </Button>
                <Button
                  type="button"
                  className="w-full gap-2"
                  disabled={sendingWa || waSent}
                  onClick={() =>
                    deliverWa({
                      phone: whatsapp,
                      clientName: nome.trim(),
                      message: template,
                    })
                  }
                >
                  {sendingWa ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MessageCircle className="h-4 w-4" />
                  )}
                  {waSent ? t('qt.alreadySent') : sendingWa ? t('qt.sending') : t('qt.sendWa')}
                </Button>
              </div>
            </>
          ) : loading ? (
            <div className="flex justify-center py-14">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <ProvisionStep active={wizard.step === 0} index={0}>
              <section className="prov-section">
                <div className="prov-section-head">{t('qt.client')}</div>
                <ContactQuickPick
                  apiUrl={apiUrl}
                  label={t('cqp.label')}
                  hint={t('cqp.hint')}
                  onPick={(c) => {
                    if (c?.name) setNome(String(c.name));
                    else if (c?.displayName) setNome(String(c.displayName));
                    if (c?.phone) setWhatsapp(String(c.phone));
                  }}
                />
                <div className="prov-grid-2">
                  <div className="prov-field">
                    <Label htmlFor="teste-nome">{t('qt.clientName')}</Label>
                    <Input
                      id="teste-nome"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder={t('qt.clientNamePh')}
                    />
                  </div>
                  <div className="prov-field">
                    <Label htmlFor="teste-wa">{t('qt.whatsapp')}</Label>
                    <InternationalPhoneField id="teste-wa" value={whatsapp} onChange={setWhatsapp} confirmation={phone} disabled={submitting} />
                  </div>
                </div>
                {whatsappAutomationAvailable ? <div className="prov-wa-toggle">
                  <Switch
                    id="teste-send-wa"
                    checked={sendAutoWa}
                    onCheckedChange={setSendAutoWa}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <Label htmlFor="teste-send-wa" className="cursor-pointer text-sm font-semibold text-foreground">
                      {t('qt.sendAuto')}
                    </Label>
                    <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                      {t('qt.sendAutoHint')}
                    </p>
                  </div>
                </div> : null}
              </section>

              </ProvisionStep>
              <ProvisionStep active={wizard.step === 1} index={1}>

              <section className="prov-section">
                <div className="prov-section-head">{t('qt.pickPlan')}</div>
                <PlanPicker
                  plans={trialPlans}
                  packages={packages}
                  selectedId={selectedPlanId}
                  onChange={(id) => {
                    setSelectedPlanId(id);
                    setContentMode('family');
                  }}
                  emptyHint={t('qt.emptyPlans')}
                  intent="quick-test"
                />
                <ContentModePicker
                  plan={selectedPlan}
                  value={contentMode}
                  onChange={setContentMode}
                  disabled={submitting}
                  className="mt-3"
                />
              </section>

              <section className="prov-section">
                <div className="prov-grid-2">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Tv className="h-4 w-4" /> {t('qt.screens')}
                      <span className="font-normal text-[10px] text-muted-foreground">
                        {t('qt.screensMax', { n: maxScreens })}
                      </span>
                    </Label>
                    <div
                      className={cn(
                        'grid gap-2',
                        telasOpts.length <= 2
                          ? 'grid-cols-2'
                          : telasOpts.length <= 4
                            ? 'grid-cols-4'
                            : 'grid-cols-5 sm:grid-cols-6',
                      )}
                    >
                      {telasOpts.map((n) => (
                        <OfficeChoiceButton
                          key={n}
                          active={n === telas}
                          onClick={() => setTelas(n)}
                          className={telasOpts.length <= 4 ? undefined : 'h-10 text-sm'}
                        >
                          {telasOpts.length <= 4
                            ? n === 1
                              ? t('qt.screen1')
                              : t('qt.screenN', { n })
                            : n}
                        </OfficeChoiceButton>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" /> {t('qt.duration')}
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {duracoes.map((d) => (
                        <OfficeChoiceButton
                          key={d.id}
                          active={d.id === duracaoId}
                          onClick={() => setDuracaoId(d.id)}
                          className="text-[15px]"
                        >
                          {shortDurLabel(d)}
                        </OfficeChoiceButton>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              </ProvisionStep>
              <ProvisionStep active={wizard.step === 2} index={2}>
              <p className="prov-selected-app">{t('pw.selectedApp')} <strong>{selectedPartnerApp?.name || t('pw.notSelected')}</strong></p>
              <section className="prov-section">
                <div className="prov-section-head">Aplicativo do cliente</div>
                <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
                  Selecione o aplicativo usado no aparelho do cliente. Confira abaixo as condições de licença: o teste do plano e o teste do aplicativo podem ter prazos diferentes.
                </p>
                <PartnerAppPicker
                  compact
                  className="provision-app-picker"
                  apps={partnerApps}
                  webPlayerOption={webPlayerOption}
                  selectedId={partnerAppId}
                  onChange={(id) => { appChosenManually.current = true; setPartnerAppId(id); }}
                />
              </section>
              {webPlayerScreenNotice && <p className="text-sm leading-relaxed text-muted-foreground">{webPlayerScreenNotice}</p>}
              </ProvisionStep>
              <ProvisionStep active={wizard.step === 3} index={3}>
                <ProvisionReview name={nome.trim()} phone={phone.formatted || whatsapp} planName={selectedPlan?.label || selectedPlan?.package_name}
                  planDetails={[[t('qt.duration'), shortDurLabel(duracao)], [t('qt.screensLabel'), effectiveScreens],
                    ...(planHasContentChoice(selectedPlan) ? [[t('pw.content'), t(contentMode === 'adult' ? 'pw.adult' : 'pw.family')]] : [])]}
                  appName={selectedPartnerApp?.name} app={selectedPartnerApp} sendAuto={sendAutoWa && whatsappAutomationAvailable} onEdit={wizard.goTo} disabled={submitting} />
                {webPlayerScreenNotice && <p className="text-sm leading-relaxed text-muted-foreground">{webPlayerScreenNotice}</p>}
              </ProvisionStep>
            </>
          )}
        </div>

        <div className={cn('prov-footer', !result && 'prov-footer--guided')}>
          {inlineError && !result ? (
            <p className="prov-footer-alert" role="alert">
              {inlineError}
            </p>
          ) : null}
          {result ? (
            <>
              <Button type="button" variant="ghost" onClick={reset}>
                {t('qt.newTest')}
              </Button>
              <Button type="button" onClick={() => setOpen(false)}>
                {t('qt.done')}
              </Button>
            </>
          ) : (
            <ProvisionNavigation step={wizard.step} onChange={wizard.goTo} disabled={loading || submitting}
              onCancel={() => { setOpen(false); reset(); }}>
            <Button
              type="button"
              onClick={gerar}
              className="w-full sm:w-auto min-w-[10rem]"
              disabled={!selectedPlanId || !partnerAppId || submitting || loading}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('qt.generating')}
                </>
              ) : (
                t('qt.generate', { dur: shortDurLabel(duracao) || '' })
              )}
            </Button>
            </ProvisionNavigation>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
      <div className={cn('mt-0.5 text-sm font-semibold text-foreground', mono && 'font-mono')}>{value}</div>
    </div>
  );
}

export { EVENT_OPEN as TESTE_RAPIDO_EVENT };
